import { useEffect, useState } from "react";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../../../services/Interceptor";
import LoadingSpinner from "../../../../components/LoadingSpinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLink,
  faPlus,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import AddProfileVideoLinks from "./add-profile-video-links";
import {
  getProfileVideoLinksService,
  insertProfileVideoLinksService,
} from "../../../../services/AthleteService";
import { ToastContainer, toast } from "react-toastify";
import PremiumFeatureModal from "../../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../../store/userActions";
import { bumpMenuRefresh } from "../../../../store/slices/athleteSlice";

export default function ProfileVideosLinks() {
  const auth = useAuthUser();
  const { user = {} } = auth || {};
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const hasSubscription = useSelector((state) => state.user.hasSubscription);
  const hasUsedFreeTrial = useSelector((state) => state.user.hasUsedFreeTrial);
  const hasSubscriptionAccess =
    hasSubscription || user?.role?.toLowerCase?.() === "highschool";
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;
  const [loading, setLoading] = useState(true);
  const [profileVideoLinks, setProfileVideoLinks] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [showInfoLinks, setShowInfoLinks] = useState(false);
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);

  useEffect(() => {
    const fetchVideoLinks = async () => {
      try {
        setLoading(true);
        const response = await getProfileVideoLinksService(apiInstance);
        setProfileVideoLinks(response.data);
      } catch (error) {
        console.error("Error fetching video links:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideoLinks();
  }, [apiInstance, auth.user.id]);

  const handleAddLink = async (newLink) => {
    try {
      setLoading(true);
      await insertProfileVideoLinksService(apiInstance, newLink);
      const updatedLinks = await getProfileVideoLinksService(apiInstance);
      setProfileVideoLinks(updatedLinks.data);
      setIsOpen(false);
    } catch (error) {
      console.error("Error adding link:", error);
    } finally {
      setLoading(false);
    }
  };

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handleOpenAdd = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    setIsOpen(true);
  };

  return (
    <>
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Adding video links is locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={() => {
          setOpenPremiumFeatureModal(false);
          openSubscription();
        }}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div className="relative">
        {loading && (
          <div className="absolute top-0 left-0 bg-white/75 w-full h-full pt-24">
            <LoadingSpinner />
          </div>
        )}
        <div className="mt-4">
          <div
            className="float-right text-sm text-white border border-gray-300 p-2 rounded bg-blue-600 hover:bg-blue-800  cursor-pointer"
            onClick={handleOpenAdd}
          >
            <FontAwesomeIcon icon={faPlus} /> Add new link
          </div>
          <h2 className="font-medium mb-4 uppercase">
            Profile Video Links
            <FontAwesomeIcon
              className="ml-2 cursor-pointer text-base"
              icon={faInfoCircle}
              onClick={() => setShowInfoLinks(true)}
            />
          </h2>
          <div className="clear-both"></div>
        </div>
        <div className="clear-both"></div>
        {Object.keys(profileVideoLinks).length > 0 ? (
          <div className="w-full mx-auto space-y-2 mt-3">
            {Object.entries(profileVideoLinks).map(([key, url], index) => (
              <div
                key={key}
                className={`p-2 flex items-center space-x-4 rounded-lg  ${
                  index % 2 === 0 ? "bg-gray-100" : "bg-white"
                }`}
              >
                <div className="bg-gray-500 text-white p-1 px-2 rounded-full">
                  <FontAwesomeIcon icon={faLink} className="w-4 h-4" />
                </div>
                <div className="flex flex-col">
                  <span className="text-sm font-semibold text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, " $1").trim()}
                  </span>
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 text-sm truncate"
                  >
                    {url}
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-blue-100 border border-blue-300 text-blue-800 p-4 text-center rounded-lg">
            <span className="text-lg font-semibold">
              There are no links to display
            </span>
          </div>
        )}
        <ToastContainer />
      </div>
      <AddProfileVideoLinks
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        handleAddLink={handleAddLink}
        setProfileVideoLinks={setProfileVideoLinks}
        setLoading={setLoading}
        profileVideoLinks={profileVideoLinks}
      />

      {showInfoLinks && (
        <Modal
          title="Profile video links"
          description="Coaches will be able to follow these links to an external site where you have hosted your videos.  Please ensure that the links you post are to publicly hosted videos.  If you prefer to upload and host videos directly in this platform use the Profile Video Uploads tab."
          onClose={() => setShowInfoLinks(false)}
        />
      )}
    </>
  );
}

function Modal({ title, description, onClose }) {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg p-6 w-3/4 max-w-md">
        <h2 className="text-lg font-bold mb-4">{title}</h2>
        <p className="text-sm mb-6">{description}</p>
        <button
          onClick={onClose}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Close
        </button>
      </div>
    </div>
  );
}
